import React, {SyntheticEvent, useEffect, useRef, useState} from "react";
import {IItemContent} from "../../typescript-library";
import * as Sentry from "@sentry/browser";
import styles from "./Image.module.scss";

// 1x1 white pixel image data
export const blankImage = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

// language code, including fix for .dk, and .nl (no placeholder available)
let lang = document.body.dataset.lang || "en";
lang = lang === "da" ? "dk" : lang;
lang = lang === "nl" ? "en" : lang;

// 'No image available' placeholder png url
const placeholder = `/images/placeholder-small-${lang}.png`;

const Image: React.FC<{item: IItemContent; lazyLoading?: boolean}> = ({item, lazyLoading = true}) => {
  const {images, title, url} = item;
  const [loadedClass, setLoadedClass] = useState<string | undefined>(styles.image);
  const [isBlankImage, setIsBlankImage] = useState<boolean>(false);
  const imageRef = useRef<HTMLImageElement>(null);

  /**
   *  first off set the local blank image pixel
   *  to reserve space and to not show the alt text
   *  this lets the next useEffect set the actual image
   *  then 'onload' triggers when pic is loaded over network
   *  and handler then fades the img in
   */
  useEffect(() => {
    if (!imageRef.current) {
      return;
    }
    imageRef.current.src = blankImage;
    imageRef.current.srcset = "";
    setIsBlankImage(true);
    setLoadedClass(styles.image);
  }, [images]);

  /**
   * retrieve sources of images, including some debugging
   */
  useEffect(() => {
    // Only proceed after 1x1 pixel is shown in the real dom
    if (!imageRef.current || !isBlankImage) {
      return;
    }

    const squareFree = (images || []).find((image) => image.mediaTypeId === 100016);
    // Should never be the case
    if (!squareFree?.sizes?.length) {
      Sentry.addBreadcrumb({
        category: "no.media",
        level: "warning",
        message: "no media found for item",
        data: {
          url,
          images,
        },
      });

      return;
    }

    let image1x = squareFree.sizes.find((size) => size.name === "half-2x")?.url || placeholder;
    let image2x = squareFree.sizes.find((size) => size.name === "full-2x")?.url;

    if (__DEVELOPMENT__) {
      image1x = image1x.replace("www.", "cdn.");
      image2x = image2x?.replace("www.", "cdn.");
    }

    // wait till next animation frame so blank image has a chance to render and reside
    window.requestAnimationFrame(() => {
      if (!imageRef.current) {
        return;
      }

      setIsBlankImage(false);
      imageRef.current.src = image1x;
      if (image2x) {
        imageRef.current.srcset = `${image1x} 1x, ${image2x} 2x`;
      }
    });
  }, [isBlankImage]);

  /**
   * On load fade picture in
   */
  const onLoad = () => {
    setLoadedClass(`${styles.image} ${styles.loaded}`);
  };

  /**
   * On error set placeholder and debugging information
   */
  const onError = (evt: SyntheticEvent<HTMLImageElement>) => {
    if (!imageRef.current || evt.currentTarget.src.endsWith(placeholder)) return;

    imageRef.current.dataset.brokenSrc = imageRef.current.src;
    imageRef.current.dataset.brokenSrcSet = imageRef.current.srcset;

    imageRef.current.src = placeholder;
    imageRef.current.srcset = "";
  };

  return (
    <img
      ref={imageRef}
      alt={title}
      onLoad={onLoad}
      onError={onError}
      className={loadedClass}
      loading={lazyLoading ? "lazy" : "eager"}
    />
  );
};

export default Image;
